import { lazy, ReactNode } from 'react'

import { AuthenticatedProviders } from '@/providers'
import LoadableSuspense from '@components/Loadable/Loadable'
import PermissionsGuard, { Permissions } from '@components/PermissionsGuard/PermissionsGuard'
import { useBoolVariation } from '@launchdarkly/react-sdk'
import { isLowerEnvironment } from '@utils/EnvironmentHelper'
import ActionCenter from '@views/Analytics/ActionCenter/ActionCenter'
import DirectDealsReport from '@views/Analytics/ActionCenter/DirectDeals/DirectDealsReport'
import Analytics from '@views/Analytics/Analytics'
import MarketBaskets from '@views/Analytics/MarketBaskets/MarketBaskets'
import Matching from '@views/Analytics/Matching/Matching'
import Segments from '@views/Analytics/Segments/Segments'
import Home from '@views/Home/Home'
import Exports from '@views/MarketBaskets/Exports/Exports'
import MarketBasketProvider from '@views/MarketBaskets/MarketBasketProvider'
import Programs from '@views/MarketBaskets/Programs/Programs'
import { createBrowserRouter, Navigate, useRouteError } from 'react-router-dom'

import Login from '../auth/Login/Login'
import AuthRedirect from './AuthRedirect'
import Root from './Root'

const Reports = LoadableSuspense(lazy(() => import('@views/Reports/Reports')))
const PlatformSettings = LoadableSuspense(
	lazy(() => import('@views/PlatformSettings/PlatformSettings'))
)
const GlobalBasketSettings = LoadableSuspense(
	lazy(() => import('@views/GlobalBasketSettings/GlobalBasketSettings'))
)
const PackConversionEditor = LoadableSuspense(
	lazy(() => import('@views/GlobalBasketSettings/PackConversions/PackConversionEditor'))
)
const ProductScoreEditor = LoadableSuspense(
	lazy(() => import('@views/GlobalBasketSettings/ProductScore/ProductScoreEditor'))
)
const OppyReports = LoadableSuspense(lazy(() => import('@views/OppyReports/OppyReports')))
const OpportunitiesTable = LoadableSuspense(
	lazy(() => import('@views/OppyReports/OpportunitiesTable/OpportunitiesTable'))
)
const TaskServer = LoadableSuspense(lazy(() => import('@views/TaskServer/TaskServer')))
const NotFound = LoadableSuspense(lazy(() => import('@views/error-pages/NotFound')))
const NotAuthorized = LoadableSuspense(lazy(() => import('@views/error-pages/NotAuthorized')))
const UserNotAdded = LoadableSuspense(lazy(() => import('@views/error-pages/UserNotAdded')))
const ServiceUnavailable = LoadableSuspense(
	lazy(() => import('@views/error-pages/ServiceUnavailable'))
)
const MuiTheme = LoadableSuspense(lazy(() => import('@views/MuiTheme/MuiTheme')))
const Report = LoadableSuspense(lazy(() => import('@views/Reports/Report/Report')))
const Users = LoadableSuspense(lazy(() => import('@views/Users/Users')))
const MarketBasketsList = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/BasketList/BasketList'))
)
const NewMarketBasket = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/Form/NewMarketBasket'))
)
const EditMarketBasket = LoadableSuspense(
	lazy(() => import('@views/MarketBaskets/Form/EditMarketBasket'))
)
const MarketBasket = LoadableSuspense(lazy(() => import('@views/MarketBaskets/View/MarketBasket')))
const SwitchTracking = LoadableSuspense(lazy(() => import('@views/SwitchTracking/SwitchTracking')))
const MyProfile = LoadableSuspense(lazy(() => import('@views/MyProfile/MyProfile')))
const AboutMe = LoadableSuspense(lazy(() => import('@views/MyProfile/AboutMe/AboutMe')))
const NotificationPreferences = LoadableSuspense(
	lazy(() => import('@views/MyProfile/NotificationPreferences/NotificationPreferences'))
)
const router = createBrowserRouter([
	{
		path: '/',
		ErrorBoundary: () => {
			// This bubbles up the error to the root level
			throw useRouteError()
		},
		element: <Root />,
		children: [
			{
				path: '',
				element: <AuthRedirect />,
			},
			{
				path: '/login',
				element: <Login />,
			},
			{
				path: '/app',
				element: <AuthenticatedProviders />,
				children: [
					{
						path: '',
						element: <Home />,
					},

					{
						path: 'reports',
						element: <Reports />,
					},
					{
						path: 'reports/:reportId',
						element: <Report />,
					},
					{
						path: 'reports/:reportId/:personalizedId',
						element: <Report />,
					},
					{
						path: 'settings',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.WonderlandView]}>
								<PlatformSettings />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketsList />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/new',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<NewMarketBasket />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/analytics',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BiDashboardView]}>
								<Analytics />
							</PermissionsGuard>
						),
						children: [
							{
								index: true,
								element: <Navigate to="market-baskets" replace />,
							},
							{
								path: 'market-baskets',
								element: <MarketBaskets />,
							},
							{
								path: 'action-center',
								element: <ActionCenter />,
							},
							{
								path: 'matching',
								element: <Matching />,
							},
							{
								path: 'segments',
								element: <Segments />,
							},
						],
					},
					{
						path: 'market-baskets/analytics/direct-deals',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BiDashboardView]}>
								<DirectDealsReport />
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId/edit',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<EditMarketBasket />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<MarketBasket />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'market-baskets/:marketBasketId/exports/:exportId',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<Exports />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						// FIXME: Need to rethink how this works. It got a bit messy with the similar products.
						// We should have the BE allow us to send the row id of the sim product for the programs
						path: 'market-baskets/:marketBasketId/similar-products/:similarProductId/programs',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.BasketEdit]}>
								<MarketBasketProvider>
									<Programs />
								</MarketBasketProvider>
							</PermissionsGuard>
						),
					},
					{
						path: 'switch-tracking',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.SwitchTracking]}>
								<FeatureFlagRoute flag="feature-art-1567-switch-tracking">
									<SwitchTracking />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'global-basket-settings',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<GlobalBasketSettings />
							</PermissionsGuard>
						),
					},
					{
						path: 'global-basket-settings/pack-conversion-editor',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<FeatureFlagRoute flag="feature-art-1447-pack-conversion-editor">
									<PackConversionEditor />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'global-basket-settings/product-score-editor',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.ProductScoreEdit]}>
								<FeatureFlagRoute flag="ART-1621-product-score-control-panel">
									<ProductScoreEditor />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'oppy-reports',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<FeatureFlagRoute flag="feature-art-1567-switch-tracking">
									<OppyReports />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'oppy-reports/opportunities',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.MarketBasketSettings]}>
								<FeatureFlagRoute flag="feature-art-1567-switch-tracking">
									<OpportunitiesTable />
								</FeatureFlagRoute>
							</PermissionsGuard>
						),
					},
					{
						path: 'task-server',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.TaskServerView]}>
								<TaskServer />
							</PermissionsGuard>
						),
					},
					{
						path: 'user-management',
						element: (
							<PermissionsGuard showWhenHasPermission={[Permissions.ManageUsers]}>
								<Users />
							</PermissionsGuard>
						),
					},
					{
						path: 'my-profile',
						element: (
							<FeatureFlagRoute flag="feature-art-1588-in-app-notifications">
								<MyProfile />
							</FeatureFlagRoute>
						),
						children: [
							{
								index: true,
								element: <Navigate to="about-me" replace />,
							},
							{
								path: 'about-me',
								element: <AboutMe />,
							},
							{
								path: 'notifications',
								element: <NotificationPreferences />,
							},
						],
					},
				],
			},
			...(isLowerEnvironment
				? [
						{
							path: 'muitheme',
							element: <MuiTheme />,
						},
					]
				: []),
			{
				path: '*',
				children: [
					{
						path: 'notauthorized',
						element: <NotAuthorized />,
					},
					{
						path: '503',
						element: <ServiceUnavailable />,
					},
					{
						path: '403',
						element: <UserNotAdded />,
					},
					{
						path: '*',
						element: <NotFound />,
					},
				],
			},
		],
	},
])

export default router

// Leave when FF is removed
function FeatureFlagRoute({ flag, children }: { flag: string; children: ReactNode }) {
	const enabled = useBoolVariation(flag, false)
	if (!enabled) return <NotFound />
	return children
}
